import React, { useState, useEffect } from 'react';
import "./Messenger.css";
import Conversations from '../components/Conversations/NewConversations';
import { useAuthContext } from '../hooks/useAuthContext';
import ChatBox from '../components/Message/NewChatBox';
import { useMessengerContext } from '../hooks/useMessengerContext';
import useSocket from '../hooks/useSocket';
import ChatProfile from '../components/Message/ChatProfile';
import SuggestionProfile from '../components/Message/SuggestionProfile';
import SkipConfirmationModal from '../components/SkipConfirmationModal';
import { getAuth } from 'firebase/auth';

const Messenger = () => {
  const { state, dispatch } = useMessengerContext();
  const { currentConversation, conversations } = state;
  const { user } = useAuthContext();

  // Activate the useSocket hook
  const { isSocketConnected } = useSocket(user?.userId, currentConversation?._id);

  const [activeComponent, setActiveComponent] = useState('messenger-conversations');
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentSuggestionProfile, setCurrentSuggestionProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Popup close handler
  const closePopup = () => setShowWelcomePopup(false);

  // Initialize hash and check for onboarding redirect
  useEffect(() => {
    // Read current hash and set activeComponent accordingly
    const currentHash = window.location.hash.replace('#', '');
    
    if (currentHash === 'messenger-chatbox') {
      setActiveComponent('messenger-chatbox');
    } else if (currentHash === 'messenger-conversations') {
      setActiveComponent('messenger-conversations');
    } else {
      // Set default hash if not present
      window.location.hash = 'messenger-conversations';
      setActiveComponent('messenger-conversations');
    }
    
    if (window.location.search.toLowerCase().includes('from=onboarding')) {
      // Removed: setShowWelcomePopup(true); - popup no longer needed
    }
  }, []);

  // Auto-select community bot conversation when coming with communityId parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromOnboarding = urlParams.get('from') === 'onboarding';
    const fromAdditionalCommunity = urlParams.get('from') === 'additional-community';
    const communityId = urlParams.get('communityId');
    const action = urlParams.get('action');
    
    console.log(`[messenger load debug] 🔍 URL Parameters:`, {
      fromOnboarding,
      fromAdditionalCommunity, 
      communityId,
      action,
      conversationsLength: conversations.length
    });
    
    // Auto-select if coming from specific flows OR if there's a communityId parameter
    if (communityId && conversations.length > 0) {
      
      console.log(`[messenger load debug] 🔄 Auto-selecting community bot conversation for communityId: ${communityId}`);
      
      // Find the community bot conversation for the specified community
      const communityBotConversation = conversations.find(conversation => 
        conversation.communityId === communityId && 
        conversation.members.includes(conversation.communityId)
      );
      
      if (communityBotConversation) {
        console.log(`[messenger load debug] 🔄 Found community bot conversation:`, communityBotConversation._id);
        // Auto-select the community bot conversation and switch to chatbox
        dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: communityBotConversation });
        
        // Also switch to chatbox view for immediate interaction
        setActiveComponent('messenger-chatbox');
        window.location.hash = 'messenger-chatbox';
        
        // From the email link {{profileUpdateInMessengerLink}}: the bot starts
        // asking for what their profile is missing — once per link opened
        // (the backend also won't start it twice).
        if (action === 'completeProfile' && user?.userId) {
          const onceKey = `profileFlowStarted:${communityBotConversation._id}:${window.location.search}`;
          let already = false;
          try { already = sessionStorage.getItem(onceKey) === '1'; sessionStorage.setItem(onceKey, '1'); } catch { /* storage off: the backend guard still applies */ }
          if (!already) {
            let tries = 0;
            const startWhenConnected = () => {
              tries += 1;
              if (isSocketConnected || tries >= 20) startBotProfileFlow(communityId, communityBotConversation._id);
              else setTimeout(startWhenConnected, 500);
            };
            startWhenConnected();
          }
        }

        // If action=connect OR coming from additional-community, trigger welcome message from community bot
        if ((action === 'connect' || fromAdditionalCommunity) && user?.userId) {
          console.log(`[messenger load debug] 🤖 Triggering welcome message for ${action === 'connect' ? 'action=connect' : 'fromAdditionalCommunity'}`);
          
          // Wait for socket connection before triggering welcome message
          let attempts = 0;
          const maxAttempts = 20; // Max 10 seconds
          
          const waitForSocketAndTrigger = () => {
            attempts++;
            console.log(`[messenger load debug] 🤖 Socket connection check attempt ${attempts}/${maxAttempts} - isSocketConnected: ${isSocketConnected}`);
            
            if (isSocketConnected) {
              console.log(`[messenger load debug] 🤖 Socket connected after ${attempts} attempts, executing welcome message trigger`);
              triggerBotWelcomeMessage(communityId, communityBotConversation._id);
            } else if (attempts >= maxAttempts) {
              console.log(`[messenger load debug] 🤖 Max attempts reached, triggering welcome message anyway`);
              triggerBotWelcomeMessage(communityId, communityBotConversation._id);
            } else {
              console.log(`[messenger load debug] 🤖 Socket not connected yet, retrying in 500ms`);
              setTimeout(waitForSocketAndTrigger, 500);
            }
          };
          
          // Start checking socket connection
          waitForSocketAndTrigger();
        }
      } else {
        console.log(`[messenger load debug] 🔄 No community bot conversation found for communityId: ${communityId}`);
      }
    }
  }, [conversations, dispatch, user?.userId, isSocketConnected]);

  // Function to trigger bot welcome message for connect action
  const triggerBotWelcomeMessage = async (communityId, conversationId) => {
    try {
      console.log(`[bot welcome debug] 🤖 Sending welcome message for community ${communityId}`);
      
      // Get authentication token
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/message/triggerWelcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          communityId: communityId,
          conversationId: conversationId,
          userId: user.userId
        })
      });

      if (response.ok) {
        console.log(`[bot welcome debug] ✅ Welcome message triggered successfully`);
        const result = await response.json();
        console.log(`[bot welcome debug] 🤖 Response:`, result);
      } else {
        console.log(`[bot welcome debug] ❌ Failed to trigger welcome message:`, response.status);
        const errorText = await response.text();
        console.log(`[bot welcome debug] ❌ Error response:`, errorText);
      }
    } catch (error) {
      console.error(`[bot welcome debug] ❌ Error triggering welcome message:`, error);
    }
  };

  // Asks the community bot to start filling in their profile with them.
  const startBotProfileFlow = async (communityId, conversationId) => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/message/startProfileFlow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ communityId, conversationId }),
      });
      if (!res.ok) console.error('[profile flow] start failed:', res.status);
    } catch (error) {
      console.error('[profile flow] start failed:', error);
    }
  };

  // Enhanced component handlers with smooth transitions
  const handleConversationSelect = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-chatbox');
      window.location.hash = 'messenger-chatbox';
      setIsTransitioning(false);
    }, 150);
  };

  const handleBackToMenu = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-conversations');
      window.location.hash = 'messenger-conversations';
      setIsTransitioning(false);
    }, 150);
  };

  const handleBackToChatBox = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-chatbox');
      window.location.hash = 'messenger-chatbox';
      setIsTransitioning(false);
    }, 150);
  };

  const handleToChatProfile = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-profile');
      window.location.hash = 'messenger-profile';
      setIsTransitioning(false);
    }, 150);
  };

  const handleToSuggestionProfile = (suggestionProfile) => {
    setCurrentSuggestionProfile(suggestionProfile);
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-suggestion-profile');
      window.location.hash = 'messenger-suggestion-profile';
      setIsTransitioning(false);
    }, 150);
  };

  const handleBackFromSuggestionProfile = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveComponent('messenger-chatbox');
      window.location.hash = 'messenger-chatbox';
      setCurrentSuggestionProfile(null);
      setIsTransitioning(false);
    }, 150);
  };

  // Remove auto-scroll as it's disruptive to UX
  // useEffect(() => {
  //   if (activeComponent === 'chatBox' || activeComponent === 'chatProfile') {
  //     window.scrollTo(0, document.body.scrollHeight);
  //   }
  // }, [activeComponent]);

  // Modal content for welcome
  const welcomeModalContent = {
    title: "Chat with your community bot!",
    message: "To get introduced and meet people, chat with the community bot in messenger.",
    cancelText: "Close",
    confirmText: false

  };

  // Handler for confirm - auto-opens the community bot conversation
  const handleWelcomeConfirm = () => {
    setShowWelcomePopup(false);
    
    // Get community ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const communityId = urlParams.get('communityId');
    
    if (communityId && conversations.length > 0) {
      // Find the community bot conversation for the specified community
      const communityBotConversation = conversations.find(conversation => 
        conversation.communityId === communityId && 
        conversation.members.includes(conversation.communityId)
      );
      
      if (communityBotConversation) {
        // Auto-select the community bot conversation
        dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: communityBotConversation });
        // Switch to chatbox view
        handleConversationSelect();
      }
    }
  };

  // Determine if navbar should be hidden on mobile
  const urlParams = new URLSearchParams(window.location.search);
  const fromOnboarding = urlParams.get('from') === 'onboarding';
  
  // Hide navbar on mobile when:
  // 1. In chatbox, profile, or suggestion profile views (not in conversations list)
  // 2. OR when coming from onboarding (even if in conversations list initially)
  const shouldHideNavbarOnMobile = isMobile && (
    activeComponent === 'messenger-chatbox' ||
    activeComponent === 'messenger-profile' ||
    activeComponent === 'messenger-suggestion-profile' ||
    fromOnboarding
  );

  return (
    <div className={`messenger-container ${isTransitioning ? 'messenger-transitioning' : ''} ${shouldHideNavbarOnMobile ? 'navbar-hidden' : ''}`}>
      {/* Welcome Modal */}
      <SkipConfirmationModal
        isOpen={showWelcomePopup}
        onCancel={closePopup}
        onConfirm={handleWelcomeConfirm}
        content={welcomeModalContent}
      />

      {/* Conversations Panel */}
      <div className={`messenger-conversations-panel ${activeComponent !== 'messenger-conversations' ? 'messenger-panel-hidden' : 'messenger-panel-active'}`}>
        <div className="messenger-conversations-wrapper">
          <Conversations onConversationSelectForCSS={handleConversationSelect} />
        </div>
      </div>

      {/* Chat Box Panel */}
      <div className={`messenger-chatbox-panel ${activeComponent !== 'messenger-chatbox' ? 'messenger-panel-hidden' : 'messenger-panel-active'}`}>
        <div className="messenger-chatbox-wrapper">
          {currentConversation ? (
            <ChatBox 
              onBackToChatMenuForCSS={handleBackToMenu} 
              onToChatProfileForCSS={handleToChatProfile}
              onToSuggestionProfileForCSS={handleToSuggestionProfile}
              isActive={activeComponent === 'messenger-chatbox'}
            />
          ) : (
            <div className="messenger-empty-state">
              <div className="messenger-empty-icon">💬</div>
              <h3 className="messenger-empty-title">Welcome to Messenger</h3>
              <p className="messenger-empty-message">
                Click on a conversation to start chatting with community members and bots
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Profile Panel */}
      <div className={`messenger-profile-panel ${activeComponent !== 'messenger-profile' ? 'messenger-panel-hidden' : 'messenger-panel-active'} ${activeComponent === 'messenger-conversations' ? 'messenger-panel-desktop-hidden' : ''}`}>
        <div className="messenger-profile-wrapper">
          <ChatProfile onBackToChatBoxForCSS={handleBackToChatBox} />
        </div>
      </div>

      {/* Suggestion Profile Panel */}
      <div className={`messenger-suggestion-profile-panel ${activeComponent !== 'messenger-suggestion-profile' ? 'messenger-panel-hidden' : 'messenger-panel-active'} ${activeComponent === 'messenger-conversations' ? 'messenger-panel-desktop-hidden' : ''}`}>
        <div className="messenger-suggestion-profile-wrapper">
          {currentSuggestionProfile && (
            <SuggestionProfile 
              onBackToChatBoxForCSS={handleBackFromSuggestionProfile}
              suggestionProfile={currentSuggestionProfile}
              onProfileResponse={(userId, isInterested) => {
                console.log("[profileinterest debug] 🎯 Messenger.js onProfileResponse called with:", { userId, isInterested });
                console.log("[profileinterest debug] 🎯 Messenger.js current userInterests before dispatch:", state.userInterests);
                dispatch({ type: 'SET_USER_INTEREST', payload: { userId, isInterested }});
                console.log("[profileinterest debug] 🎯 Messenger.js dispatch called for SET_USER_INTEREST");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Messenger;

//  import React, { useState, useEffect } from 'react';
// import "./Tester1.css";
// import Conversations from '../components/Conversations/NewConversations';
// import { useAuthContext } from '../hooks/useAuthContext';
// import ChatBox from '../components/Message/NewChatBox';
// import { useMessengerContext } from '../hooks/useMessengerContext';
// import useSocket from '../hooks/useSocket';
// import ChatProfile from '../components/Message/ChatProfile';

// const Messenger = () => {
//   const { state } = useMessengerContext();
//   const { currentConversation } = state;
//   const { user } = useAuthContext();

//   // Activate the useSocket hook
//   useSocket(user?.userId, currentConversation?._id);

//   const [activeComponent, setActiveComponent] = useState('chatMenu');
//   const [showWelcomePopup, setShowWelcomePopup] = useState(false);

//   // Popup close handler
//   const closePopup = () => setShowWelcomePopup(false);

//   // Check for onboarding redirect
//   useEffect(() => {
//     if (window.location.search.toLowerCase().includes('from=onboarding')) {
//       setShowWelcomePopup(true);
//     }
//   }, []);

//   // Component handlers
//   const handleConversationSelect = () => setActiveComponent('chatBox');
//   const handleBackToMenu = () => setActiveComponent('chatMenu');
//   const handleBackToChatBox = () => setActiveComponent('chatBox');
//   const handleToChatProfile = () => setActiveComponent('chatProfile');

//   // Auto-scroll
//   useEffect(() => {
//     if (activeComponent === 'chatBox' || activeComponent === 'chatProfile') {
//       window.scrollTo(0, document.body.scrollHeight);
//     }
//   }, [activeComponent]);

//   return (
//     <div className="messenger">
//       {/* Welcome Popup Overlay */}
//       {showWelcomePopup && (
//         <div className="popup-overlay">
//           <div className="welcome-popup">
//             <p>Welcome to Messenger!</p>
//             <button onClick={closePopup}>Close</button>
//           </div>
//         </div>
//       )}

//       {/* Conversations Panel */}
//       <div className={`chatMenu ${activeComponent !== 'chatMenu' ? 'hideOnMobile' : ''}`}>
//         <div className="chatMenuWrapper">
//           <Conversations onConversationSelectForCSS={handleConversationSelect} />
//         </div>
//       </div>

//       {/* Chat Box */}
//       <div className={`chatBox ${activeComponent !== 'chatBox' ? 'hideOnMobile' : ''}`}>
//         <div className="chatBoxWrapper">
//           {currentConversation ? (
//             <ChatBox 
//               onBackToChatMenuForCSS={handleBackToMenu} 
//               onToChatProfileForCSS={handleToChatProfile} 
//             />
//           ) : (
//             <div className="centered-text">
//               <p>&larr;</p>
//               <p>Click on a message to the left to find suggestions from community bots and chat with connections :)</p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Chat Profile */}
//       <div className={`chatProfile ${activeComponent !== 'chatProfile' ? 'hideOnMobile' : ''} ${activeComponent === 'chatMenu' ? 'hideOnDesktop' : ''}`}>
//         <div className="chatProfileWrapper">
//           <ChatProfile onBackToChatBoxForCSS={handleBackToChatBox} />
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Messenger;


// import React, { useState, useEffect, useRef } from 'react';
// import { isMobile } from 'react-device-detect';
// import "./Tester1.css";
// import Conversations from '../components/Conversations/NewConversations';
// import { useAuthContext } from '../hooks/useAuthContext';
// import ChatBox from '../components/Message/NewChatBox';
// import { useMessengerContext } from '../hooks/useMessengerContext';
// import useSocket from '../hooks/useSocket';
// import ChatProfile from '../components/Message/ChatProfile';

// const Messenger = () => {
//   const { state, dispatch } = useMessengerContext();
//   const { currentConversation } = state;
//   const { user } = useAuthContext();
//   const messagesEndRef = useRef(null);
  
//   // Socket integration
//   const { sendMessage, arrivalMessage } = useSocket(user?.userId, currentConversation?._id);
//   const [activeComponent, setActiveComponent] = useState('chatMenu');

//   // Improved scrolling with ref
//   const scrollToBottom = () => {
//     messagesEndRef.current?.scrollIntoView({
//       behavior: 'smooth',
//       block: 'end'
//     });
//   };

//   // Handle new messages
//   useEffect(() => {
//     if (arrivalMessage && currentConversation?._id === arrivalMessage.conversationId) {
//       dispatch({ type: 'ADD_MESSAGE', payload: arrivalMessage });
//       setTimeout(scrollToBottom, 100);
//     }
//   }, [arrivalMessage, currentConversation]);

//   // Responsive layout handling
//   const getComponentVisibility = (targetComponent) => {
//     if (isMobile) {
//       return activeComponent === targetComponent ? '' : 'hideOnMobile';
//     }
//     return '';
//   };

//   const handleConversationSelect = (conversation) => {
//     dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: conversation });
//     setActiveComponent('chatBox');
//     setTimeout(scrollToBottom, 300);
//   };

//   return (
//     <div className="messenger">
//       {/* Conversations Panel */}
//       <div className={`chatMenu ${getComponentVisibility('chatMenu')}`}>
//         <div className="chatMenuWrapper">
//           <Conversations
//             onConversationSelectForCSS={handleConversationSelect} // Original prop name
//           />
//         </div>
//       </div>

//       {/* Rest of your code remains unchanged */}
//     </div>
//   );
// };

// export default Messenger;

//  import React, { useState, useEffect } from 'react';
// import "./Tester1.css";
// import Conversations from '../components/Conversations/NewConversations';
// import { useAuthContext } from '../hooks/useAuthContext';
// import ChatBox from '../components/Message/NewChatBox';
// import { useMessengerContext } from '../hooks/useMessengerContext';
// import useSocket from '../hooks/useSocket';
// import ChatProfile from '../components/Message/ChatProfile';

// const Messenger = () => {
//   const { state } = useMessengerContext();
//   const { currentConversation } = state;
//   const { user } = useAuthContext();

//   // Activate the useSocket hook
//   useSocket(user?.userId, currentConversation?._id);

//   const [activeComponent, setActiveComponent] = useState('chatMenu');
//   const [showWelcomePopup, setShowWelcomePopup] = useState(false);

//   // Popup close handler
//   const closePopup = () => setShowWelcomePopup(false);

//   // Check for onboarding redirect
//   useEffect(() => {
//     if (window.location.search.toLowerCase().includes('from=onboarding')) {
//       setShowWelcomePopup(true);
//     }
//   }, []);

//   // Component handlers
//   const handleConversationSelect = () => setActiveComponent('chatBox');
//   const handleBackToMenu = () => setActiveComponent('chatMenu');
//   const handleBackToChatBox = () => setActiveComponent('chatBox');
//   const handleToChatProfile = () => setActiveComponent('chatProfile');

//   // Auto-scroll
//   useEffect(() => {
//     if (activeComponent === 'chatBox' || activeComponent === 'chatProfile') {
//       window.scrollTo(0, document.body.scrollHeight);
//     }
//   }, [activeComponent]);

//   return (
//     <div className="messenger">
//       {/* Welcome Popup Overlay */}
//       {showWelcomePopup && (
//         <div className="popup-overlay">
//           <div className="welcome-popup">
//             <p>Welcome to Messenger!</p>
//             <button onClick={closePopup}>Close</button>
//           </div>
//         </div>
//       )}

//       {/* Conversations Panel */}
//       <div className={`chatMenu ${activeComponent !== 'chatMenu' ? 'hideOnMobile' : ''}`}>
//         <div className="chatMenuWrapper">
//           <Conversations onConversationSelectForCSS={handleConversationSelect} />
//         </div>
//       </div>

//       {/* Chat Box */}
//       <div className={`chatBox ${activeComponent !== 'chatBox' ? 'hideOnMobile' : ''}`}>
//         <div className="chatBoxWrapper">
//           {currentConversation ? (
//             <ChatBox 
//               onBackToChatMenuForCSS={handleBackToMenu} 
//               onToChatProfileForCSS={handleToChatProfile} 
//             />
//           ) : (
//             <div className="centered-text">
//               <p>&larr;</p>
//               <p>Click on a message to the left to find suggestions from community bots and chat with connections :)</p>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Chat Profile */}
//       <div className={`chatProfile ${activeComponent !== 'chatProfile' ? 'hideOnMobile' : ''} ${activeComponent === 'chatMenu' ? 'hideOnDesktop' : ''}`}>
//         <div className="chatProfileWrapper">
//           <ChatProfile onBackToChatBoxForCSS={handleBackToChatBox} />
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Messenger;
